import React, { useEffect, useState } from 'react';
import { useProgress } from '../contexts/ProgressContext';
import { useWallet } from '../contexts/WalletContext';
import { useNotification } from '../contexts/NotificationContext';
import { hasCheckedInToday, setCheckIn, getCheckInStats } from '../utils/progressUtils';
import { tutorials } from '../data/tutorials';

const ProgressIndicator = () => {
  const { progress, updateProgress } = useProgress();
  const { connected, publicKey } = useWallet();
  const { notifySuccess, notifyError, notifyWarning, notifyInfo } = useNotification();
  const [checkedInToday, setCheckedInToday] = useState(false);

  useEffect(() => {
    // Check if user has already checked in today
    if (connected && publicKey) {
      const walletAddress = publicKey.toString();
      setCheckedInToday(hasCheckedInToday(walletAddress));
    } else {
      setCheckedInToday(false);
    }
  }, [connected, publicKey]);

  const handleCheckIn = async () => {
    try {
      if (!connected || !publicKey) {
        notifyWarning(
          'Wallet Not Connected',
          'Please connect your wallet to check in and track your progress.'
        );
        return;
      }

      const walletAddress = publicKey.toString();
      
      // Check if already checked in today
      if (hasCheckedInToday(walletAddress)) {
        const stats = getCheckInStats(walletAddress);
        notifyInfo(
          'Already Checked In Today!',
          `Your current streak: ${stats.streakDays} days. Come back tomorrow to continue your streak!`,
          6000
        );
        return;
      }

      // Perform local check-in
      const checkInResult = setCheckIn(walletAddress);
      
      if (checkInResult) {
        setCheckedInToday(true);

        // Update progress context with new streak
        updateProgress({
          stats: {
            ...progress.stats,
            streakDays: checkInResult.streakDays,
            lastActiveDate: checkInResult.lastCheckInDate
          }
        });

        // Show success message with streak info
        const title = checkInResult.streakDays === 1
          ? 'Check-in Successful!'
          : `${checkInResult.streakDays} Day Streak!`;
        
        const description = checkInResult.streakDays === 1
          ? `Start your learning journey! Progress saved locally for wallet ${walletAddress.slice(0, 8)}...${walletAddress.slice(-6)}`
          : `Total check-ins: ${checkInResult.totalCheckIns}. Progress saved locally for ${walletAddress.slice(0, 8)}...${walletAddress.slice(-6)}`;
        
        notifySuccess(title, description, 6000);
      } else {
        throw new Error('Check-in failed');
      }
    } catch (err) {
      console.error('Check-in error:', err);
      notifyError(
        'Check-in Failed',
        err.message || 'Unable to complete check-in. Please try again.'
      );
    }
  };

  const getEarnedAchievements = () => {
    return Object.values(progress.achievements).filter(a => a.earned).length;
  };

  const getCompletedTutorials = () => {
    return Object.values(progress.tutorials).filter(t => t.completed).length;
  };

  const getTotalTutorials = () => {
    // Count total tutorials from the tutorials data
    return tutorials ? tutorials.length : 0;
  };

  const getConceptsMastered = () => {
    if (!progress || !progress.concepts) return [];
    try {
      return Object.entries(progress.concepts)
        .filter(([_, data]) => data && data.level >= 1)
        .map(([concept, data]) => ({ concept, level: data.level || 1, xp: data.xp || 0 }));
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Error getting concepts:', e);
      }
      return [];
    }
  };

  const getProgressPercentage = () => {
    const achievements = getEarnedAchievements();
    const completedTutorials = getCompletedTutorials();
    const totalTutorials = getTotalTutorials();
    const conceptsMastered = getConceptsMastered().length;
    const totalXP = progress.stats.totalXP || 0;
    
    // Calculate progress based on meaningful completion metrics
    // Tutorials are the primary driver of progress (60% weight)
    const tutorialScore = totalTutorials > 0 
      ? (completedTutorials / totalTutorials) * 60
      : 0;
    
    // Concepts mastered (20% weight - requires actual game completion)
    const conceptScore = Math.min(conceptsMastered * 4, 20);
    
    // Achievements (15% weight - meaningful milestones only)
    const achievementScore = Math.min(achievements * 1.5, 15);
    
    // XP bonus (5% weight - requires significant learning)
    const xpScore = Math.min(totalXP / 100, 5);
    
    const totalScore = tutorialScore + conceptScore + achievementScore + xpScore;
    
    return Math.min(totalScore, 100);
  };

  if (!connected) {
    return (
      <div className="progress-indicator">
        <div className="progress-header">
          <h3>Your Progress</h3>
          <p>Start learning to track your achievements and progress!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="progress-indicator">
      <div className="progress-header">
        <h3>Your Progress</h3>
        <div className="progress-stats">
          <div className="stat-item">
            <span className="stat-value">{getEarnedAchievements()}</span>
            <span className="stat-label">Achievements</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{getCompletedTutorials()}/{getTotalTutorials()}</span>
            <span className="stat-label">Tutorials</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{progress.stats.totalXP || 0}</span>
            <span className="stat-label">Total XP</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{getConceptsMastered().length}</span>
            <span className="stat-label">Concepts</span>
          </div>
        </div>
      </div>
      
      <div className="progress-bar-container">
        <div className="progress-bar">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${getProgressPercentage()}%` }}
          ></div>
        </div>
        <span className="progress-percentage">{Math.round(getProgressPercentage())}% Complete</span>
      </div>

      {/* Check-in Button */}
      <button
        onClick={handleCheckIn}
        disabled={checkedInToday}
        style={{
          width: '100%',
          marginTop: '16px',
          padding: '10px 16px',
          background: checkedInToday 
            ? 'rgba(16, 185, 129, 0.15)' 
            : 'rgba(249, 115, 22, 0.1)',
          border: checkedInToday
            ? '1px solid rgba(16, 185, 129, 0.4)'
            : '1px solid rgba(249, 115, 22, 0.3)',
          borderRadius: '8px',
          color: checkedInToday ? '#10b981' : '#f97316',
          fontSize: '14px',
          fontWeight: '500',
          cursor: checkedInToday ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease'
        }}
        onMouseEnter={(e) => {
          if (!checkedInToday) {
            e.target.style.background = 'rgba(249, 115, 22, 0.15)';
            e.target.style.borderColor = 'rgba(249, 115, 22, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          if (!checkedInToday) {
            e.target.style.background = 'rgba(249, 115, 22, 0.1)';
            e.target.style.borderColor = 'rgba(249, 115, 22, 0.3)';
          }
        }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          {checkedInToday ? (
            <>
              <path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2"/>
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2"/>
            </>
          ) : (
            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
          )}
        </svg>
        <span>{checkedInToday ? 'Checked In' : 'Check-in'}</span>
      </button>
    </div>
  );
};

export default ProgressIndicator;
